import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { kbMaterialsPath } from "@/lib/kb-routes";
import type { HermesMediaCandidate, HermesMediaCandidatesPayload } from "@/lib/hermes-media-candidates";
import { HermesMediaCandidateCard } from "./HermesMediaCandidateCard";

type HermesMediaCandidatesGalleryProps = {
  accountId: string;
  payload: HermesMediaCandidatesPayload;
};

export function mediaCandidatesVisibleItems(items: HermesMediaCandidate[]): HermesMediaCandidate[] {
  return items.slice(0, 6);
}

export function mediaCandidatesShouldShowViewAll(params: {
  truncated: boolean;
  totalCount: number;
  visibleCount: number;
}): boolean {
  return params.truncated || params.totalCount > params.visibleCount;
}

export function mediaCandidatesViewAllPath(params: {
  accountId: string;
  brandId?: string;
  viewAllScope: HermesMediaCandidatesPayload["viewAllScope"];
}): string {
  const { accountId, brandId, viewAllScope } = params;
  if (viewAllScope === "brand" && brandId) {
    return kbMaterialsPath({ accountId, brandId });
  }
  return kbMaterialsPath({ accountId });
}

function dialogDescription(candidate: HermesMediaCandidate): string {
  if (candidate.kind === "image") return "图片预览";
  if (candidate.kind === "video") return "视频预览";
  return "音频预览";
}

export function HermesMediaCandidatesGallery({
  accountId,
  payload,
}: HermesMediaCandidatesGalleryProps) {
  const navigate = useNavigate();
  const { brandId } = useParams();
  const [selected, setSelected] = useState<HermesMediaCandidate | null>(null);
  const visibleItems = useMemo(() => mediaCandidatesVisibleItems(payload.items), [payload.items]);
  const shouldShowViewAll = mediaCandidatesShouldShowViewAll({
    truncated: payload.truncated,
    totalCount: payload.totalCount,
    visibleCount: visibleItems.length,
  });

  function handleViewAll() {
    const href = mediaCandidatesViewAllPath({
      accountId,
      brandId: brandId ?? undefined,
      viewAllScope: payload.viewAllScope,
    });
    navigate(href);
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {visibleItems.map((candidate) => (
          <HermesMediaCandidateCard
            key={candidate.slug}
            accountId={accountId}
            candidate={candidate}
            compact
            onClick={() => setSelected(candidate)}
          />
        ))}
      </div>

      {shouldShowViewAll ? (
        <div className="flex justify-start">
          <Button type="button" variant="outline" size="sm" onClick={handleViewAll}>
            查看全部 {payload.totalCount} 个
          </Button>
        </div>
      ) : null}

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="flex max-h-[85vh] w-full max-w-4xl flex-col gap-4">
          {selected ? (
            <>
              <DialogHeader className="pr-8">
                <DialogTitle>{selected.name?.trim() || selected.slug}</DialogTitle>
                <DialogDescription>{dialogDescription(selected)}</DialogDescription>
              </DialogHeader>
              <div className="min-h-0 overflow-y-auto rounded-xl border border-border/60 bg-muted/20 p-3">
                <HermesMediaCandidateCard
                  accountId={accountId}
                  candidate={selected}
                  previewMode="dialog"
                />
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
